import React, { useEffect, useState, useCallback } from 'react';
import { billsAPI } from '../../utils/api';
import { 
  fmtCurrency as fmt, formatDate, formatDateOnly as fmtDateOnly, formatTimeOnly as fmtTimeOnly 
} from '../../utils/helpers';

const STATUS_COLORS = {
  pending: { bg: '#FFF7E6', text: '#D97706', label: 'Pending' },
  paid: { bg: '#ECFDF5', text: '#059669', label: 'Paid' },
  overdue: { bg: '#FEF2F2', text: '#DC2626', label: 'Overdue' },
  cancelled: { bg: '#F3F4F6', text: '#6B7280', label: 'Cancelled' },
};

export default function BillsTab() {
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [paying, setPaying] = useState(null);

  const loadBills = useCallback(async () => {
    try {
      const res = await billsAPI.list(filter === 'all' ? null : filter);
      if (res.data.success) {
        setBills(res.data.data.bills);
        setSummary(res.data.data.summary);
      }
    } catch { }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    loadBills();
  }, [loadBills]);

  // Real-time synchronization
  useEffect(() => {
    const refresh = () => {
      console.log('Real-time bill update received. Refreshing...');
      loadBills();
    };
    window.addEventListener('weighment:bill_new', refresh);
    window.addEventListener('weighment:bill_deleted', refresh);
    window.addEventListener('weighment:deleted', refresh);
    window.addEventListener('app:bill_refresh', refresh);

    return () => {
      window.removeEventListener('weighment:bill_new', refresh);
      window.removeEventListener('weighment:bill_deleted', refresh);
      window.removeEventListener('weighment:deleted', refresh);
      window.removeEventListener('app:bill_refresh', refresh);
    };
  }, [loadBills]);

  const handleMarkPaid = async (bill) => {
    setPaying(bill.id);
    try {
      await billsAPI.markPaid(bill.id);
      setBills(prev => prev.map(b => b.id === bill.id
        ? { ...b, status: 'paid', paid_at: new Date().toISOString() } : b));
      setSelected(null);
    } catch { }
    setPaying(null);
  };
  const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'paid', label: 'Paid' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', background: '#F9FAFB' }}>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, padding: '14px 16px 4px', flexShrink: 0 }}>
          <SummaryCard
            label="Pending"
            value={fmt(summary.pending_amount || 0)}
            count={summary.pending_count || 0}
            color="#D97706"
            bg="#FFF7E6"
          />
          <SummaryCard
            label="Overdue"
            value={fmt(summary.overdue_amount || 0)}
            count={summary.overdue_count || 0}
            color="#DC2626"
            bg="#FEF2F2"
          />
        </div>
      )}

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', flexShrink: 0, overflowX: 'auto' }}>
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: filter === f.id ? '2px solid #128C7E' : '2px solid #E5E7EB',
              background: filter === f.id ? '#128C7E' : 'white',
              color: filter === f.id ? 'white' : '#374151',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >{f.label}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner" />
        </div>
      ) : bills.length === 0 ? (
        <div className="empty-state">
          <span className="icon">🧾</span>
          <h3>No bills found</h3>
          <p>{filter === 'all' ? 'You have no bills yet' : `No ${filter} bills`}</p>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {Object.entries(
            bills.reduce((acc, b) => {
              const day = fmtDateOnly(b.created_at);
              if (!acc[day]) acc[day] = [];
              acc[day].push(b);
              return acc;
            }, {})
          ).map(([day, dayBills]) => (
            <div key={day}>
              <div style={{
                padding: '10px 16px', fontSize: 11, fontWeight: 800, color: '#667781',
                background: '#F3F4F6', position: 'sticky', top: 0, zIndex: 1,
                textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #E5E7EB'
              }}>
                🗓️ {day}
              </div>
              {dayBills.map(bill => {
                const s = STATUS_COLORS[bill.status] || STATUS_COLORS.pending;
                return (
                  <div
                    key={bill.id}
                    onClick={() => setSelected(bill)}
                    style={{
                      margin: '0 12px 10px', borderRadius: 12, background: 'white',
                      boxShadow: '0 1px 4px rgba(0,0,0,.07)',
                      padding: '14px 16px', cursor: 'pointer',
                      borderLeft: `4px solid ${s.text}`,
                      transition: 'box-shadow .15s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 700, fontSize: 14, color: '#111B21', marginBottom: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {bill.title}
                        </div>
                        {bill.partner_name && (
                          <div style={{ fontSize: 12, color: '#667781', marginBottom: 4 }}>
                            {bill.partner_name}
                          </div>
                        )}
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                          Ref: {bill.reference} • {fmtTimeOnly(bill.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#111B21' }}>
                          {fmt(bill.amount, bill.currency)}
                        </div>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: s.bg, color: s.text
                        }}>
                          {s.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <BillModal
          bill={selected}
          fmt={fmt}
          fmtDate={fmtDate}
          onClose={() => setSelected(null)}
          onMarkPaid={() => handleMarkPaid(selected)}
          paying={paying === selected.id}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, count, color, bg }) {
  return (
    <div style={{
      flex: 1, background: bg, borderRadius: 10, padding: '10px 14px',
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#111B21' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#667781', marginTop: 2 }}>{count} bill{count !== 1 ? 's' : ''}</div>
    </div>
  );
}

function BillModal({ bill, fmt, fmtDate, onClose, onMarkPaid, paying }) {
  const s = STATUS_COLORS[bill.status] || STATUS_COLORS.pending;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 520, background: 'white',
          borderRadius: '20px 20px 0 0', padding: '24px 24px 32px',
          animation: 'slideInFromBottom .25s cubic-bezier(.4,0,.2,1)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{ width: 36, height: 4, background: '#E5E7EB', borderRadius: 2, margin: '0 auto 20px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#111B21', margin: 0 }}>{bill.title}</h2>
            {bill.partner_name && <div style={{ fontSize: 13, color: '#667781', marginTop: 4 }}>{bill.partner_name}</div>}
          </div>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 12,
            background: s.bg, color: s.text
          }}>
            {s.label}
          </span>
        </div>

        <div style={{ fontSize: 32, fontWeight: 900, color: '#111B21', marginBottom: 20 }}>
          {fmt(bill.amount, bill.currency)}
        </div>

        <Row label="Reference" value={bill.reference} />
        {bill.description && <Row label="Description" value={bill.description} />}
        {bill.due_date && <Row label="Due Date" value={fmtDate(bill.due_date)} />}
        {bill.paid_at && <Row label="Paid On" value={fmtDate(bill.paid_at)} />}
        <Row label="Issued" value={fmtDate(bill.created_at)} />

        {bill.media_url && (
          <a href={bill.media_url} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, color: '#128C7E', fontSize: 14,
              fontWeight: 600, marginTop: 16, textDecoration: 'none'
            }}>
            📄 View Invoice
          </a>
        )}

        {bill.status === 'pending' || bill.status === 'overdue' ? (
          <button
            onClick={onMarkPaid}
            disabled={paying}
            style={{
              marginTop: 24, width: '100%', padding: '14px', borderRadius: 12,
              background: paying ? '#9CA3AF' : '#128C7E', color: 'white',
              border: 'none', fontSize: 15, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {paying ? <><span className="spinner" style={{ width: 18, height: 18, borderColor: 'rgba(255,255,255,.4)', borderTopColor: 'white' }} /> Processing…</> : '✓ Mark as Paid'}
          </button>
        ) : (
          <button onClick={onClose} style={{
            marginTop: 24, width: '100%', padding: '14px', borderRadius: 12,
            background: '#F3F4F6', color: '#374151',
            border: 'none', fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}>
            Close
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '8px 0',
      borderBottom: '1px solid #F3F4F6', fontSize: 14
    }}>
      <span style={{ color: '#667781', fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#111B21', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}
